import React, { useContext } from "react";
import { DataLayerContext } from "../dataLayer/DataLayer";
import "./puzzleScreen.css";
import { AppBar } from "./components/appBar";
import { ScorePanel } from "./components/scorePanel";
import { PuzzleBoardContainer } from "./components/puzzleBoardContainer";
import { PointerStateManagerContext } from "../gameLogic/pointerStateManager";
import { getPuzzleTimeRemainingInSeconds } from "../gameLogic/puzzleTimer";

export function PuzzleScreen() {
  const dataLayer = useContext(DataLayerContext);
  const pointerStateManager = useContext(PointerStateManagerContext);

  /**
   * The number of seconds spent on this puzzle in previous sessions.
   * A new session starts if the user navigates away then comes back.
   */
  const [timeInPreviousSessionsInSeconds] = React.useState<number>(
    dataLayer.currentPuzzle.value?.timeElapsedInSeconds || 0,
  );
  const [timestampAtScreenLoad] = React.useState<number>(Date.now());
  const [timeRemainingInSeconds, setTimeRemainingInSeconds] = React.useState<number>(
    dataLayer.currentPuzzle.value
      ? getPuzzleTimeRemainingInSeconds(dataLayer.currentPuzzle.value)
      : 0,
  );

  /** Updates timeElapsedInSeconds in the current puzzle */
  const updateTimeElapsedInSeconds = () => {
    if (dataLayer.currentPuzzle.value) {
      dataLayer.currentPuzzle.value.timeElapsedInSeconds =
        timeInPreviousSessionsInSeconds + (Date.now() - timestampAtScreenLoad) / 1000;
    }
  };

  /** Saves the current state */
  const save = () => {
    updateTimeElapsedInSeconds();
    dataLayer.save();
  };

  const onBackClicked = () => {
    save();
    dataLayer.currentPuzzle.value = undefined;
    dataLayer.navigateBack();
  };

  React.useEffect(() => {
    let expired = false;
    const finishPuzzle = () => {
      if (expired) {
        return;
      }
      expired = true;
      pointerStateManager?.onClear();
      save();
      dataLayer.navigateTo("SubmittingSolution");
    };

    if (timeRemainingInSeconds === 0) {
      finishPuzzle();
      return;
    }

    const handle = setInterval(() => {
      const timeOnPageInSeconds = (Date.now() - timestampAtScreenLoad) / 1000;
      const totalTimeInSeconds = timeOnPageInSeconds + timeInPreviousSessionsInSeconds;
      const newTimeRemainingInSeconds = Math.max(
        0,
        (dataLayer.currentPuzzle.value?.timeLimitInSeconds || 0) - totalTimeInSeconds,
      );
      setTimeRemainingInSeconds(newTimeRemainingInSeconds);

      // Save on every tick so that if the user leaves or reloades, their state is not stale
      save();

      if (newTimeRemainingInSeconds === 0) {
        finishPuzzle();
      }
    }, 1000);
    return () => {
      clearInterval(handle);
    };
  }, [timestampAtScreenLoad, setTimeRemainingInSeconds, pointerStateManager]);

  return (
    <div className="screen play">
      <AppBar brand onBack={onBackClicked} />
      {dataLayer.currentPuzzle.value ? (
        <div className="playArea">
          <div className="playAreaColumn">
            <ScorePanel
              puzzle={dataLayer.currentPuzzle.value}
              timeRemainingInSeconds={timeRemainingInSeconds}
            ></ScorePanel>

            <PuzzleBoardContainer
              puzzle={dataLayer.currentPuzzle.value}
              isReadOnly={!dataLayer.currentPuzzle.value.isPlayable}
            ></PuzzleBoardContainer>

            <div className="play__rarity-note">Rarity bonuses are awarded tomorrow</div>
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}
